import { forwardRef, ReactNode, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { AccordionIcon, useDisclosure } from "@chakra-ui/react";
import { DragDropContext, Draggable, Droppable, DropResult } from "react-beautiful-dnd";
import { FiAlertCircle, FiPlus, FiUpload } from "react-icons/fi";

import { Heading } from "components/Heading";
import { Switch } from "components/Switch";
import { Button } from "newui/components/Button";
import { AutoSaveIndicator } from "newui/components/ui/AutoSave/AutoSaveIndicator";
import { FileUpload, UploadedFile, UploadStatus } from "newui/components/FileUpload";
import { Modal } from "newui/components/ui/Modal";
import { useAutoSave } from "newui/hooks/useAutoSave";

import { SummaryTable } from "../../../components/QuoteTable/SummaryTable";
import { CreateSummaryTableModal } from "../../../components/QuoteTable/CreateSummaryTableModal";
import { CreateQuoteTableModal } from "../../../components/QuoteTable/CreateQuoteTableModal";
import type { QuoteTableConfig } from "../../../components/QuoteTable/CreateQuoteTableModal";
import { AiSpreadsheetImport } from "../../../components/QuoteTable/AiSpreadsheetImport";
import {
	useProcurerQuoteTables,
	useCreateQuoteTable,
	useUpdateQuoteTable,
	useDeleteQuoteTable,
	useCopyQuoteTable,
	useReorderQuoteTables,
	useCreateQuoteRow,
	useDeleteQuoteRow,
	useBatchCreateQuoteRows,
	useBatchDeleteQuoteRows,
	useBatchUpdateQuoteRows,
	useCreateQuoteColumn,
	useUpdateQuoteColumn,
	useDeleteQuoteColumn,
	useReorderQuoteColumns,
	useReorderQuoteRows,
	useExistingValueColumns,
	useSummaryTables,
	useCreateSummaryTable,
	useDeleteSummaryTable,
	useSaveQuoteAsTemplate,
	useUpdateQuoteSettings,
	useSyncQuoteTableLayout,
	useBatchUpdateCells,
	useUpdateSummaryTable,
} from "../../hooks/useProcurerQuotes";
import type { RfxQuoteColumnType, SummaryTableConfig } from "../../../Submission/types/quote.type";
import { useUnitsAndMeasures } from "newui/store/common-types";
import { getQuoteTemplates } from "../../../api/quote-templates-api";
import type { QuoteTemplate } from "../../../api/quote-templates-api";
import { RichTextEditor } from "newui/components/ui/RichTextEditor";
import { Rfx } from "newui/features/Rfx/types/rfx.types";
import type { QuoteResourceType } from "../../../api/procurer-quotes-api";
import { UploadFile } from "types/storage.types";
import { FormLabel } from "newui/components/ui/FormLabel";
import { QuoteTableCard } from "./components/QuoteTableCard";
import { isRfxStatusMatch } from "newui/features/Rfx/utils";
import { RFX_STATUS } from "utils/constants";
import { LoadingSpinner } from "components/Loading/LoadingSpinner";
import { useQuoteTabValidation } from "./useQuoteTabValidation";
import SingleAccordion from "newui/components/SingleAccordion";
import { useRfxSubmissionData } from "newui/features/Rfx/data-provider/RfxSubmissionDataProvider";
import { ProcurerQuoteTableQuickActions } from "./components/ProcurerQuoteTableQuickActions";

// TODO: Remove cast once this JS component is migrated to TypeScript
// const FileUpload = UnTypedFileUpload as React.FC<any>;

export interface ProcurerQuoteTabHandle {
	saveQuoteTab: () => Promise<boolean>;
	/** Triggers validation UI and returns true if valid */
	validate: () => boolean;
}

interface ProcurerQuoteSettings {
	allow_flexible_quote?: boolean;
	quoteFile?: UploadFile;
	quoteInstructions?: string | null;
	quoteType?: "form" | "file";
}

interface ProcurerQuoteTabProps {
	resourceId?: number | null;
	resourceType?: QuoteResourceType;
	subTitlePage: string;
	titlePage: string;
	previewRfxHandler: () => void;
}

const renderButtonContent = (isOpen: boolean, resourceType: QuoteResourceType): ReactNode => {
	return <><FiAlertCircle className="shrink-0" />
		<span className="pl-2">Please resolve all errors before sending your RFx - click to {isOpen ? "hide" : "view"} errors</span><AccordionIcon /></>;
}

const ProcurerQuoteTabInner: React.ForwardRefRenderFunction<ProcurerQuoteTabHandle, ProcurerQuoteTabProps> = ({
	subTitlePage,
	titlePage,
	resourceType = "rfx",
	resourceId: resourceIdProp,
	previewRfxHandler,
}, ref) => {
	const { rfxData, updateLocalRfxData } = useRfxSubmissionData();
	const rfxId = resourceIdProp ?? rfxData?.id;
	const isTemplate = resourceType === "rfx-template";
	const isDraft = isTemplate
		? rfxData?.status !== "locked"
		: isRfxStatusMatch(rfxData?.status, RFX_STATUS.DRAFT);

	// --- Local state for the 3 scalar quote settings (initialised from rfxData) ---
	const [quoteSettings, setQuoteSettings] = useState<ProcurerQuoteSettings>(() => ({
		allow_flexible_quote: rfxData?.allow_flexible_quote,
		quoteInstructions: rfxData?.quoteInstructions,
		quoteType: rfxData?.quoteType ?? "form",
		quoteFile: rfxData?.quoteFile,
	}));
	const [areQuoteSettingsDirty, setAreQuoteSettingsDirty] = useState(false);
	const updateSettings = useUpdateQuoteSettings(rfxId, resourceType);

	const updateQuoteSetting = useCallback((field: keyof ProcurerQuoteSettings, value: any) => {
		setQuoteSettings((prev) => ({ ...prev, [field]: value }));
		setAreQuoteSettingsDirty(true);
	}, []);

	// Track whether quote_type has been persisted at least once this session.
	// Covers the case where DB has null quote_type but the frontend defaults to "form".
	const hasPersistedQuoteType = useRef(false);

	const saveSettings = useCallback(() => {
		if (!areQuoteSettingsDirty && hasPersistedQuoteType.current) {
			return;
		}
		hasPersistedQuoteType.current = true;

		const apiPayload: Record<string, unknown> = {
			quote_type: quoteSettings.quoteType,
			quote_instructions: quoteSettings.quoteInstructions,
			allow_flexible_quote: quoteSettings.allow_flexible_quote,
		};
		// Only send quote_file_path when clearing — uploads are handled by FileUpload component
		if (quoteSettings.quoteFile === null) {
			apiPayload.quote_file_path = null;
		}
		updateSettings.mutate(apiPayload);
	}, [updateSettings, areQuoteSettingsDirty, quoteSettings]);

	const [pendingDeleteSummaryId, setPendingDeleteSummaryId] = useState<number | null>(null);

	// React-Query state for quote tables
	const { data: quoteTables, isLoading, refetch: refetchQuoteTables } = useProcurerQuoteTables(rfxId, resourceType);
	const createTable = useCreateQuoteTable(rfxId, resourceType);
	const updateTable = useUpdateQuoteTable(rfxId, resourceType);
	const deleteTable = useDeleteQuoteTable(rfxId, resourceType);
	const copyTable = useCopyQuoteTable(rfxId, resourceType);
	const reorderTables = useReorderQuoteTables(rfxId, resourceType);
	const createRow = useCreateQuoteRow(rfxId, resourceType);
	const batchCreateRows = useBatchCreateQuoteRows(rfxId, resourceType);
	const deleteRow = useDeleteQuoteRow(rfxId, resourceType);
	const batchDeleteRows = useBatchDeleteQuoteRows(rfxId, resourceType);
	const batchUpdateRows = useBatchUpdateQuoteRows(rfxId, resourceType);
	const createColumn = useCreateQuoteColumn(rfxId, resourceType);
	const updateColumn = useUpdateQuoteColumn(rfxId, resourceType);
	const deleteColumn = useDeleteQuoteColumn(rfxId, resourceType);
	const reorderColumns = useReorderQuoteColumns(rfxId, resourceType);
	const reorderRows = useReorderQuoteRows(rfxId, resourceType);
	const syncLayout = useSyncQuoteTableLayout(rfxId, resourceType);
	const batchCells = useBatchUpdateCells(rfxId, resourceType);
	const { data: allExistingValueColumns } = useExistingValueColumns(rfxId, resourceType);
	const { values: unitOptions } = useUnitsAndMeasures();
	const { data: summaryTables } = useSummaryTables(rfxId, resourceType);
	const createSummaryTable = useCreateSummaryTable(rfxId, resourceType);
	const updateSummaryTable = useUpdateSummaryTable(rfxId, resourceType);
	const deleteSummaryTable = useDeleteSummaryTable(rfxId, resourceType);
	const summaryTableModal = useDisclosure();
	const [editingSummaryTable, setEditingSummaryTable] = useState<SummaryTableConfig | null>(null);
	const aiImportModal = useDisclosure();
	const [templates, setTemplates] = useState<QuoteTemplate[]>([]);
	const refetchTemplates = useCallback(() => {
		getQuoteTemplates().then(setTemplates).catch(() => setTemplates([]));
	}, []);
	const saveAsTemplate = useSaveQuoteAsTemplate(rfxId, refetchTemplates, resourceType);
	const [showCreateTableModal, setShowCreateTableModal] = useState(false);

	// --- Dirty cell values for procurer-editable columns (e.g. additional description) ---
	// Map of quoteId -> { rowId -> { columnId -> value } }
	const [dirtyCells, setDirtyCells] = useState<
		Record<number, Record<number, Record<number, string>>>
	>({});
	const dirtyCellsRef = useRef(dirtyCells);
	dirtyCellsRef.current = dirtyCells;

	// Validation — self-contained, no Formik dependency
	const {
		hasQuoteErrors,
		errorMessages,
		showBanner,
		tableHasError,
		getTableFieldError,
		getRowErrors,
		validate: validateQuoteTab,
	} = useQuoteTabValidation(quoteSettings, quoteTables, dirtyCells);

	// Map of quoteId -> { rowId -> { name?, unit_id? } }
	const [dirtyRows, setDirtyRows] = useState<
		Record<number, Record<number, { unit_id?: number | null; name?: string; }>>
	>({});
	const dirtyRowsRef = useRef(dirtyRows);
	dirtyRowsRef.current = dirtyRows;

	const areQuoteRowsDirty = useMemo(() => {
		return Object.values(dirtyRows).some(
			(tableRows) => Object.keys(tableRows).length > 0
		);
	}, [dirtyRows]);

	const flushDirtyRows = useCallback(async (): Promise<boolean | undefined> => {
		const snapshot = dirtyRowsRef.current;
		if (!Object.keys(snapshot).length) {
			return undefined;
		}
		// Clear dirty state immediately so new edits during save are tracked separately
		setDirtyRows({});

		const promises = Object.entries(snapshot).map(([quoteIdStr, rowMap]) => {
			const quoteId = Number(quoteIdStr);
			const rows = Object.entries(rowMap).map(([rowIdStr, payload]) => ({
				id: Number(rowIdStr),
				...payload,
			}));
			if (rows.length === 0) {
				return Promise.resolve(undefined);
			}
			return batchUpdateRows.mutateAsync({ quoteId, rows });
		});
		const results = await Promise.all(promises);
		return results.filter((v) => v !== undefined).pop();
	}, [batchUpdateRows]);

	const areCellsDirty = useMemo(() => {
		return Object.values(dirtyCells).some(
			(tableRows) => Object.values(tableRows).some(
				(rowCols) => Object.keys(rowCols).length > 0
			)
		);
	}, [dirtyCells]);

	const flushDirtyCells = useCallback(async (): Promise<boolean | undefined> => {
		const snapshot = dirtyCellsRef.current;
		if (!Object.keys(snapshot).length) {
			return undefined;
		}
		setDirtyCells({});

		const promises = Object.entries(snapshot).map(([quoteIdStr, rowMap]) => {
			const quoteId = Number(quoteIdStr);
			const cells = Object.entries(rowMap).flatMap(([rowIdStr, colMap]) =>
				Object.entries(colMap).map(([colIdStr, value]) => ({
					row_id: Number(rowIdStr),
					column_id: Number(colIdStr),
					value,
				}))
			);
			if (cells.length === 0) {
				return Promise.resolve(undefined);
			}
			return batchCells.mutateAsync({ quoteId, cells });
		});
		const results = await Promise.all(promises);
		return results.filter((v) => v !== undefined).pop();
	}, [batchCells]);

	// Build columnValues from API data + local dirty overrides
	const buildColumnValues = useCallback((quoteTable: { id: number; rows: Array<{ id: number; cells?: Array<{ rfxQuoteColumnId: number; value: string | null }> }> }) => {
		const values: Record<number, Record<number, string>> = {};
		for (const row of quoteTable.rows || []) {
			values[row.id] = {};
			for (const cell of row.cells || []) {
				values[row.id][cell.rfxQuoteColumnId] = cell.value ?? "";
			}
			// Override with dirty local values
			const dirtyRow = dirtyCells[quoteTable.id]?.[row.id];
			if (dirtyRow) {
				for (const [colId, val] of Object.entries(dirtyRow)) {
					values[row.id][Number(colId)] = val;
				}
			}
		}
		return values;
	}, [dirtyCells]);

	const handleCellValueChange = useCallback((quoteId: number, rowId: number, columnId: number, _field: string, value: string) => {
		setDirtyCells((prev) => ({
			...prev,
			[quoteId]: {
				...prev[quoteId],
				[rowId]: {
					...prev[quoteId]?.[rowId],
					[columnId]: value,
				},
			},
		}));
	}, []);

	const handlePasteRows = useCallback(async (
		quoteId: number,
		currentItemId: number,
		currentRowCells: Record<number, string>,
		newRows: Array<{ cells: Record<number, string>; unitId?: number | null }>,
		currentRowCount: number
	) => {
		// 1. Update current row's cells locally (instant, no API call)
		for (const [colIdStr, value] of Object.entries(currentRowCells)) {
			handleCellValueChange(quoteId, currentItemId, Number(colIdStr), "value", value);
		}

		if (newRows.length === 0) {
			return;
		}

		// 2. Find existing empty rows after the paste target to fill first
		const quoteTable = quoteTables?.find((qt) => qt.id === quoteId);
		const rows = quoteTable?.rows ?? [];
		const pasteIdx = rows.findIndex((r) => r.id === currentItemId);
		const rowsAfter = pasteIdx >= 0 ? rows.slice(pasteIdx + 1) : [];

		const isRowEmpty = (row: typeof rows[number]): boolean => {
			if (!row.cells || row.cells.length === 0) {
				return true;
			}
			return row.cells.every((c) => !c.value || !c.value.trim());
		};

		// Split pasted lines: fill empties first, create genuinely new rows for the rest
		const cellUpdates: Array<{ column_id: number; row_id: number; value: string }> = [];
		const rowsToCreate: typeof newRows = [];
		let emptyIdx = 0;

		for (const pastedRow of newRows) {
			// Find next empty row after paste target
			while (emptyIdx < rowsAfter.length && !isRowEmpty(rowsAfter[emptyIdx])) {
				emptyIdx++;
			}

			if (emptyIdx < rowsAfter.length) {
				// Fill existing empty row
				const existingRow = rowsAfter[emptyIdx];
				for (const [colIdStr, value] of Object.entries(pastedRow.cells)) {
					cellUpdates.push({
						column_id: Number(colIdStr),
						row_id: existingRow.id,
						value,
					});
					// Also update local state instantly
					handleCellValueChange(quoteId, existingRow.id, Number(colIdStr), "value", value);
				}
				emptyIdx++;
			} else {
				// No more empty rows — create a new one
				rowsToCreate.push(pastedRow);
			}
		}

		// 3. Persist cell updates for filled rows
		if (cellUpdates.length > 0) {
			await batchCells.mutateAsync({ quoteId, cells: cellUpdates });
		}

		// 4. Batch-create genuinely new rows
		if (rowsToCreate.length > 0) {
			const rowPayloads = rowsToCreate.map((pastedRow, i) => ({
				unit_id: pastedRow.unitId ?? null,
				sort_order: currentRowCount + i + 1,
				cells: Object.entries(pastedRow.cells).map(([colIdStr, value]) => ({
					column_id: Number(colIdStr),
					value,
				})),
			}));

			await batchCreateRows.mutateAsync({ quoteId, rows: rowPayloads });
		}
	}, [handleCellValueChange, batchCreateRows, batchCells, quoteTables]);

	const handleBulkDeleteRows = useCallback(async (quoteId: number, rowIds: number[]) => {
		await batchDeleteRows.mutateAsync({ quoteId, rowIds });
	}, [batchDeleteRows]);

	const saveQuoteTab = useCallback(async (): Promise<boolean> => {
		let saveResult = false;
		let serverValid: boolean | undefined;
		try {
			validateQuoteTab();
			saveSettings();
			setAreQuoteSettingsDirty(false);
			const [rowsValid, cellsValid] = await Promise.all([flushDirtyRows(), flushDirtyCells()]);
			// Use the last server-computed validation value (both evaluate full DB state)
			serverValid = cellsValid ?? rowsValid;
			saveResult = true;
		} catch {
			// save failed
		}
		// Server value is authoritative; fall back to client-side validation
		// when nothing was flushed (e.g., auto-save already persisted changes)
		const isValid = serverValid ?? !hasQuoteErrors;
		updateLocalRfxData({
			...rfxData,
			quoteType: quoteSettings.quoteType,
			quoteInstructions: quoteSettings.quoteInstructions,
			allow_flexible_quote: quoteSettings.allow_flexible_quote,
			quoteFile: quoteSettings.quoteFile,
			isQuoteTabValid: isValid
		} as Rfx);
		return saveResult;
	}, [flushDirtyRows, flushDirtyCells, saveSettings, validateQuoteTab, hasQuoteErrors, rfxData, updateLocalRfxData, quoteSettings]);

	useImperativeHandle(ref, () => ({ saveQuoteTab, validate: validateQuoteTab }), [saveQuoteTab, validateQuoteTab]);

	const { isSaving, lastSavedAt } = useAutoSave({
		isDirty: areQuoteSettingsDirty || areQuoteRowsDirty || areCellsDirty,
		onSave: saveQuoteTab,
		debounceMs: 10000,
	});

	const handleRowUpdate = useCallback(
		(quoteId: number, rowId: number, payload: { unit_id?: number | null; name?: string; }) => {
			setDirtyRows((prev) => ({
				...prev,
				[quoteId]: {
					...(prev[quoteId] || {}),
					[rowId]: {
						...(prev[quoteId]?.[rowId] || {}),
						...payload,
					},
				},
			}));
		},
		[]
	);

	// Fetch company templates for the Add Table picker
	useEffect(() => {
		refetchTemplates();
	}, [refetchTemplates]);



	const tableTypeOptions = [
		{ label: "Create my own quote format", value: "form", icon: "text-input" },
		{ label: "Upload Template", value: "file", icon: "template" },
	];

	const handleAddTableFromModal = useCallback(async (config: QuoteTableConfig) => {
		const sortOrder = (quoteTables?.length || 0) + 1;
		try {
			await createTable.mutateAsync({
				name: config.name,
				description: config.description,
				sort_order: sortOrder,
				required: true,
				columns: config.columns.map((col, idx) => ({
					column_type: col.columnType,
					name: col.label,
					sort_order: idx,
				})),
				default_rows: config.defaultRows?.map((row, idx) => {
					// Translate the row's localId-keyed cells into sort_order-keyed
					// cells so the backend can resolve them to column IDs (sort_order
					// is the only column-identifier shared with the create payload).
					const cells: Record<number, string> = {};
					if (row.cells) {
						config.columns.forEach((col, colIdx) => {
							const value = row.cells?.[col.localId];
							if (value !== undefined && value !== "") {
								cells[colIdx] = value;
							}
						});
					}
					return {
						unit_id: row.unit_id,
						sort_order: idx,
						...(Object.keys(cells).length > 0 ? { cells } : {}),
					};
				}),
			});
			// Only close on success — keeps the modal open in its "Saving..."
			// state for the duration of the request, and leaves the user's
			// configuration intact if the request fails so they can retry.
			setShowCreateTableModal(false);
		} catch {
			// Mutation hook surfaces its own error toast; leave modal open.
		}
	}, [createTable, quoteTables?.length]);

	// No auto-create: procurers start with an empty state and use the Add Table
	// or Import from Spreadsheet buttons to create tables manually.

	const handleDragEnd = useCallback(
		(result: DropResult) => {
			if (!result.destination || !quoteTables) {
				return;
			}

			const startIndex = result.source.index;
			const endIndex = result.destination.index;

			if (startIndex === endIndex) {
				return;
			}

			const reordered = Array.from(quoteTables);
			const [moved] = reordered.splice(startIndex, 1);
			reordered.splice(endIndex, 0, moved);

			const quoteIds = reordered.map((qt) => qt.id);
			reorderTables.mutate(quoteIds);
		},
		[quoteTables, reorderTables]
	);

	const handleFileUploadComplete = useCallback(
		(value: UploadedFile[]) => {
			const uploadedFile = value?.[0] ?? null;

			if (!uploadedFile) {
				updateQuoteSetting("quoteFile", null);
				return;
			}

			// Only persist once the upload has completed — earlier callbacks
			// (IDLE / UPLOADING) don't have server data yet.
			if (uploadedFile.status === UploadStatus.UPLOADED && uploadedFile.path) {
				updateQuoteSetting("quoteFile", {
					id: uploadedFile.id,
					file_name: uploadedFile.name,
					file_path: uploadedFile.path,
					file_size: uploadedFile.size,
				});
			}
		},
		[updateQuoteSetting]
	);

	const nonRemovedTables = useMemo(
		() => quoteTables?.filter((qt) => qt.id != null) || [],
		[quoteTables]
	);

	const quoteTableNames = useMemo(() => {
		const map: Record<number, string> = {};
		nonRemovedTables.forEach((qt) => {
			map[qt.id] = qt.name || `Table ${qt.id}`;
		});
		return map;
	}, [nonRemovedTables]);

	const handleSummaryTableSubmit = useCallback(
		(name: string, quoteTableIds: number[], valueColumnNames: string[], includeColumnTotals: boolean) => {
			if (editingSummaryTable) {
				updateSummaryTable.mutate({
					summaryTableId: editingSummaryTable.id,
					payload: {
						name,
						quote_ids: quoteTableIds,
						value_column_names: valueColumnNames,
						include_column_totals: includeColumnTotals,
					},
				});
				setEditingSummaryTable(null);
			} else {
				createSummaryTable.mutate({
					name,
					quote_ids: quoteTableIds,
					value_column_names: valueColumnNames,
					include_column_totals: includeColumnTotals,
				});
			}
		},
		[createSummaryTable, updateSummaryTable, editingSummaryTable]
	);

	// Wait for parent data before rendering — avoids null rfxId / empty settings
	if (!rfxData || !rfxId) {
		return (
			<div className="flex w-full items-center justify-center py-16">
				<LoadingSpinner spinnerSize="xl" />
			</div>
		);
	}

	return (
		<div className="flex w-full flex-col">
			<div className="py-8">
				<div className="text-xl font-semibold text-primary-900">
					<Heading
						fontSize="36px"
						color="#003CE9"
					>
						{titlePage}
					</Heading>
				</div>
				<div className="mt-1 whitespace-pre-line font-roboto text-sm leading-[1.5rem] text-tertiary-600">
					{subTitlePage}
				</div>
			</div>

			{hasQuoteErrors && !isLoading && (
				<div
					className="mb-4 rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-700"
					data-testid="quote-validation-banner"
				>
					<SingleAccordion initialOpen={true} hoverColor="red.100" renderButtonContent={(isOpen) => renderButtonContent(isOpen, resourceType)}>
						<ul className="list-disc space-y-1 pl-6 pt-2">
							{errorMessages.map((msg, i) => (
								<li key={i}>{msg}</li>
							))}
						</ul>
					</SingleAccordion>
				</div>
			)}

			{isDraft && (
				<div className="my-4 flex items-center justify-start gap-3">
					<AutoSaveIndicator
						isSaving={isSaving}
						lastSavedAt={lastSavedAt}
					/>
				</div>
			)}

			<div className="space-y-5 rounded-md bg-[#EEF5FFB2] p-8 ml-2">
				<p className="text-base text-tertiary-600">
					Select an option below to get started:
				</p>
				<div className="flex flex-col items-start gap-x-12 gap-y-5 md:flex-row">
					<div className="!max-w-[340px]">
						<select
							value={quoteSettings.quoteType || "form"}
							onChange={(e) => {
								const val = e.target.value as "form" | "file";
								updateQuoteSetting("quoteType", val);
							}}
							className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm"
						>
							{tableTypeOptions.map((opt) => (
								<option key={opt.value} value={opt.value}>
									{opt.label}
								</option>
							))}
						</select>
					</div>
					<div>
						<Switch
							id="allow_flexible_quote"
							isChecked={quoteSettings.allow_flexible_quote}
							onChange={() => {
								const newValue = !quoteSettings.allow_flexible_quote;
								updateQuoteSetting("allow_flexible_quote", newValue);
							}}
							label="Allow Supplier File Upload"
							description="Allow suppliers to upload an alternative quote or supporting documentation as part of their submission."
							size="md"
							switchClassName="pt-1"
						/>
					</div>
				</div>
				<RichTextEditor
					label="Quote Instructions"
					subTitle="Describe any additional information or instructions required to help suppliers complete this Quote section."
					placeholder="(Optional) provide any additional instructions and/or information that will help suppliers complete this quote section..."
					maxLength={3000}
					value={quoteSettings.quoteInstructions || ""}
					onChange={(val: string) => {
						updateQuoteSetting("quoteInstructions", val);
					}}
				/>
			</div>



			{isDraft && quoteSettings.quoteType !== "file" && (
				<div className="my-6 flex gap-4 pl-2">
					<Button
						btntype="secondary"
						data-testid="add-table"
						onClick={() => setShowCreateTableModal(true)}
						disabled={createTable.isPending}
						type="button"
					>
						<FiPlus className="mr-1" />
						Add Quote Table
					</Button>
					<Button
						btntype="secondary"
						data-testid="add-summary-table"
						onClick={summaryTableModal.onOpen}
						type="button"
						disabled={nonRemovedTables.length < 2}
					>
						<FiPlus className="mr-1" />
						Add Summary Table
					</Button>
					<Button
						btntype="secondary"
						data-testid="import-spreadsheet"
						onClick={aiImportModal.onOpen}
						type="button"
					>
						<FiUpload className="mr-1" />
						Import from Spreadsheet
					</Button>
					<ProcurerQuoteTableQuickActions addQuoteSummaryTableAction={summaryTableModal.onOpen} addQuoteTableAction={() => setShowCreateTableModal(true)} previewAction={previewRfxHandler} />
				</div>
			)}

			<div className="relative h-full min-h-[60vh] pl-2">
				{isLoading ? (
					<LoadingSpinner />
				) : quoteSettings.quoteType !== "file" ? (
					<>
						<DragDropContext onDragEnd={handleDragEnd}>
							<Droppable
								droppableId="quote-tables-container"
								direction="vertical"
							>
								{(provided) => (
									<div
										ref={provided.innerRef}
										{...provided.droppableProps}
										className="mb-8"
									>
										{nonRemovedTables.map((quoteTable, index) => (
											<Draggable
												key={`quote-table-${quoteTable.id}`}
												draggableId={`quote-table-${quoteTable.id}`}
												index={index}
											>
												{(provided) => (
													<div
														ref={provided.innerRef}
														{...provided.draggableProps}
													>
														<QuoteTableCard
															resourceType={resourceType}
															hasError={tableHasError(index)}
															isBusy={createRow.isPending || batchCreateRows.isPending || batchDeleteRows.isPending || deleteRow.isPending}
															nameError={getTableFieldError(index, "name")}
															descError={getTableFieldError(index, "description")}
															rowErrors={getRowErrors(index)}
															disabled={!isDraft}
															dragHandleProps={isDraft ? provided.dragHandleProps : null}
															isDeletable={isDraft}
															onAddRow={() =>
																createRow.mutate({
																	quoteId: quoteTable.id,
																	payload: {
																		name: "",
																		unit_id: null,
																		sort_order: (quoteTable.rows?.length || 0) + 1,
																	},
																})
															}
															onColumnAdd={(columnType, label) =>
																createColumn.mutate({
																	quoteId: quoteTable.id,
																	columnType: columnType as RfxQuoteColumnType,
																	name: label,
																})
															}
															onColumnDelete={(columnId) =>
																deleteColumn.mutate({
																	quoteId: quoteTable.id,
																	columnId,
																})
															}
															onColumnLabelChange={(columnId, label) =>
																updateColumn.mutate({
																	quoteId: quoteTable.id,
																	columnId,
																	payload: { name: label },
																})
															}
															onColumnReorder={(columnIds) =>
																reorderColumns.mutate({
																	quoteId: quoteTable.id,
																	columnIds,
																})
															}
															onCopy={() => copyTable.mutate(quoteTable.id)}
															onDelete={() => deleteTable.mutate(quoteTable.id)}
															onDeleteRow={(rowId) =>
																deleteRow.mutate({
																	quoteId: quoteTable.id,
																	rowId,
																})
															}
															onEditLayout={(config) => {
																syncLayout.mutate({
																	quoteId: quoteTable.id,
																	payload: {
																		name: config.name,
																		description: config.description,
																		columns: config.columns.map((col, idx) => ({
																			id: col.id || undefined,
																			name: col.label,
																			column_type: col.columnType,
																			sort_order: idx + 1,
																		})),
																	},
																});
															}}
															onReorderRows={(rowIds) =>
																reorderRows.mutate({
																	quoteId: quoteTable.id,
																	rowIds,
																})
															}
															onSaveAsTemplate={(name) =>
																saveAsTemplate.mutate({ quoteId: quoteTable.id, name })
															}
															onUpdateDescription={(description) =>
																updateTable.mutate({
																	quoteId: quoteTable.id,
																	payload: { description },
																})
															}
															onUpdateName={(name) =>
																updateTable.mutate({
																	quoteId: quoteTable.id,
																	payload: { name },
																})
															}
															onToggleTotalsRow={() =>
																updateTable.mutate({
																	quoteId: quoteTable.id,
																	payload: { include_totals_row: !quoteTable.includeTotalsRow },
																})
															}
															onUpdateRow={(rowId, payload) =>
																handleRowUpdate(quoteTable.id, rowId, payload)
															}
															columnValues={buildColumnValues(quoteTable)}
															onBulkDeleteRows={(rowIds) =>
																handleBulkDeleteRows(quoteTable.id, rowIds)
															}
															onCellValueChange={(rowId, columnId, field, value) =>
																handleCellValueChange(quoteTable.id, rowId, columnId, field, value)
															}
															onPasteRows={(currentItemId, currentRowCells, newRows) =>
																handlePasteRows(
																	quoteTable.id,
																	currentItemId,
																	currentRowCells,
																	newRows,
																	quoteTable.rows?.length || 0
																)
															}
															dirtyRowValues={dirtyRows[quoteTable.id] || {}}
															quoteTable={quoteTable}
															siblingValueColumns={allExistingValueColumns || []}
															unitOptions={unitOptions}
														/>
													</div>
												)}
											</Draggable>
										))}
										{provided.placeholder}
									</div>
								)}
							</Droppable>
						</DragDropContext>

						{summaryTables && summaryTables.length > 0 && (
							<div className="mt-8">
								<h3 className="mb-4 font-roboto text-lg font-semibold text-primary-900">
									Summary Tables
								</h3>
								{summaryTables.map((st) => {
									const missingTables = st.quote_table_ids.filter(
										(id) => !quoteTableNames[id]
									);
									return (
										<div key={st.id}>
											{missingTables.length > 0 && (
												<div className="mb-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
													Warning: {missingTables.length} referenced table(s) have been deleted.
													This summary table may show incomplete data.
												</div>
											)}
											<SummaryTable
												categoryNames={st.value_column_names}
												includeColumnTotals={st.include_column_totals}
												mode="edit"
												onDelete={() => setPendingDeleteSummaryId(st.id)}
												onEdit={() => {
													setEditingSummaryTable(st);
													summaryTableModal.onOpen();
												}}
												quoteTableIds={st.quote_table_ids}
												quoteTableNames={quoteTableNames}
												resourceType={resourceType}
												rfxId={rfxId}
												summaryTableId={st.id}
												summaryTableName={st.name}
											/>
										</div>
									);
								})}

								<Modal
									isOpen={pendingDeleteSummaryId !== null}
									onClose={() => setPendingDeleteSummaryId(null)}
									title="Delete Summary Table?"
									modalType="standard"
									iconType="error"
									closeOnOverlayClick
									closeOnEsc
									primaryButton={{
										text: "Delete",
										btntype: "danger",
										isLoading: deleteSummaryTable.isPending,
										onClick: () => {
											if (pendingDeleteSummaryId !== null) {
												deleteSummaryTable.mutate(pendingDeleteSummaryId, {
													onSuccess: () => setPendingDeleteSummaryId(null),
												});
											}
										},
									}}
									secondaryButton={{
										text: "Cancel",
										onClick: () => setPendingDeleteSummaryId(null),
									}}
								>
									<>
										Are you sure you want to delete the summary table {summaryTables.find((st) => st.id === pendingDeleteSummaryId)?.name ?? ""}?
										<div>This action cannot be undone.</div>
									</>
								</Modal>
							</div>
						)}

						<CreateSummaryTableModal
							availableQuoteTables={nonRemovedTables}
							editingTable={editingSummaryTable}
							isOpen={summaryTableModal.isOpen}
							onClose={() => {
								summaryTableModal.onClose();
								setEditingSummaryTable(null);
							}}
							onSubmit={handleSummaryTableSubmit}
							resourceType={resourceType}
							rfxId={rfxId}
						/>

						<AiSpreadsheetImport
							resourceId={rfxId}
							resourceType={resourceType}
							isOpen={aiImportModal.isOpen}
							onClose={aiImportModal.onClose}
							onImportComplete={() => refetchQuoteTables()}
						/>

						<CreateQuoteTableModal
							isOpen={showCreateTableModal}
							onClose={() => {
								// Block closing while the create request is in flight so
								// the user can't dismiss the spinner and lose context.
								if (createTable.isPending) return;
								setShowCreateTableModal(false);
							}}
							onSubmit={handleAddTableFromModal}
							isSubmitting={createTable.isPending}
							templates={templates}
							unitOptions={unitOptions}
						/>
					</>
				) : (
					<div className="mt-12">
						<FormLabel
							label="Upload quote template (only 1 currently supported)"
							required
						>
						</FormLabel>
						<FileUpload extraData={isTemplate ? { rfxTemplateId: rfxId } : { rfq_id: rfxId }} onChange={handleFileUploadComplete} maxFileSizeMB={50} multiple={false} required supportsHardDelete uploadType={isTemplate ? "rfq_quote_file_template" : "rfq_quote_file"} allowFileTypes={["xlsx", "xls", "csv"]} initialValue={quoteSettings.quoteFile?.file_path ? [quoteSettings.quoteFile] : []} />
					</div>
				)}
			</div>
		</div>
	);
};

const ProcurerQuoteTab = forwardRef(ProcurerQuoteTabInner);

export { ProcurerQuoteTab };
